'use client'

import React, { FC, useEffect, useState } from 'react'
import type { Header as HeaderType } from '@payload-types'
import type { Media as MediaType } from '@payload-types'
import { Media } from '@/components/media'
import { cn } from '@/utilities/ui'
import type { CurrencyData } from '@/app/(frontend)/api/currency/route'

interface CurrencyTickerProps {
  data: HeaderType
}

export const CurrencyTicker: FC<CurrencyTickerProps> = ({ data }) => {
  const currencyTickerItems = data['currency-ticker']?.currencyTickerItems || []
  const [apiData, setApiData] = useState<Record<string, CurrencyData> | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchCurrencyData = async () => {
      try {
        setLoading(true)
        const response = await fetch('/api/currency')

        if (!response.ok) {
          throw new Error('Failed to fetch currency data')
        }

        const result = await response.json()

        if (result.success) {
          setApiData(result.data)
        } else {
          throw new Error(result.error || 'Unknown error')
        }
      } catch (err) {
        console.error('Error fetching currency data:', err)
        setError(err instanceof Error ? err.message : 'Failed to load currency data')
      } finally {
        setLoading(false)
      }
    }

    // Fetch data initially
    fetchCurrencyData()

    // Set up interval to refresh data every 5 minutes
    const intervalId = setInterval(fetchCurrencyData, 5 * 60 * 1000)

    // Clean up on unmount
    return () => clearInterval(intervalId)
  }, [])

  // Color classes based on direction
  const getColorClass = (direction: string) => {
    switch (direction) {
      case 'increase':
        return 'text-emerald-500'
      case 'decrease':
        return 'text-red-500'
      default:
        return 'text-gray-500'
    }
  }

  // If there are no items configured, don't render the ticker
  if (!currencyTickerItems || currencyTickerItems.length === 0) {
    return null
  }

  if (error) {
    return (
      <div className="col-span-12 py-1">
        <div className="flex items-center justify-center text-xs text-destructive">
          Unable to load currency data
        </div>
      </div>
    )
  }

  // Limit displayed items on smaller screens
  const displayItems = currencyTickerItems.slice(0, 3)

  return (
    <div className="col-span-12 py-2 overflow-x-auto">
      {/* Mobile view - scrollable ticker with fewer items */}
      <div className="flex md:hidden items-center gap-4 whitespace-nowrap overflow-x-auto pb-2">
        {displayItems.map((item) => {
          const mediaIcon = typeof item.icon === 'object' ? (item.icon as MediaType) : null
          const pairKey = item.pairKey || ''
          const currencyData = apiData && pairKey ? apiData[pairKey] : null

          // Skip items without a valid pairKey
          if (!pairKey) return null

          return (
            <div key={item.id} className="flex items-center gap-2 px-2">
              {mediaIcon && <Media resource={mediaIcon} className="size-4" htmlElement="span" />}
              <span className="font-semibold text-xs">
                {loading ? '...' : currencyData?.currency || pairKey}
              </span>
              {loading ? (
                <span className="text-xs text-muted-foreground">...</span>
              ) : currencyData ? (
                <span className={cn('text-xs', getColorClass(currencyData.direction))}>
                  {currencyData.price}
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">-</span>
              )}
            </div>
          )
        })}
      </div>

      {/* Desktop view - all items */}
      <div className="hidden md:flex items-center gap-4 whitespace-nowrap justify-around">
        {currencyTickerItems.map((item) => {
          const mediaIcon = typeof item.icon === 'object' ? (item.icon as MediaType) : null
          const pairKey = item.pairKey || ''
          const currencyData = apiData && pairKey ? apiData[pairKey] : null

          // Skip items without a valid pairKey
          if (!pairKey) return null

          return (
            <div key={item.id} className="flex flex-col items-center gap-2">
              <div className="flex items-center justify-center gap-2">
                {mediaIcon && <Media resource={mediaIcon} className="size-5" htmlElement="span" />}
                <span className="font-semibold text-sm">
                  {loading ? '...' : currencyData?.currency || pairKey}
                </span>
              </div>
              <div className="flex items-center gap-1">
                {loading ? (
                  <span className="text-xs text-muted-foreground">Loading...</span>
                ) : currencyData ? (
                  <>
                    <span className={cn('text-xs', getColorClass(currencyData.direction))}>
                      {currencyData.change}
                    </span>
                    <span className="text-xs">{currencyData.price}</span>
                  </>
                ) : (
                  <span className="text-xs text-muted-foreground">No data</span>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
